import flow from 'lodash/flow'
import has from 'lodash/has'
import isNaN from 'lodash/isNaN'
import isNumber from 'lodash/isNumber'
import isNull from 'lodash/isNull'
import isPlainObject from 'lodash/isPlainObject'
import mapKeys from 'lodash/mapKeys'
import mapValues from 'lodash/mapValues'

import {authGet, authPost, monolithPost} from './fetch'
import {ebb, get, logBadResponse, onStatus, shim, shimJson} from './helpers'
import Height from '../types/Height'
import services from '../config/services'

// -- shimJsonData()

const getData = ebb(get, 'data')

const errorIfNotPlainObject = (obj) => {
  if (!isPlainObject(obj)) throw new Error('Invalid format received. Expected object.')
  return obj
}

/*
 * In: {data: {...}}
 * Out: {{...}}
 */

const shimJsonData = (fn) => shimJson(flow([getData, errorIfNotPlainObject, fn]))

// combine heightFeet: '5' and heightInches: '8' into height: '5\'8"'
const heightIn = (obj) =>
  has(obj, 'heightFeet') && has(obj, 'heightInches')
    ? {
        ...obj,
        ...{
          height: new Height(obj.heightFeet, obj.heightInches).toKey(),
        },
      }
    : obj

// split height: '5\'8"' into heightFeet: '5' and heightInches: '8'
const heightOut = (obj) => ({
  ...obj,
  ...{
    heightFeet: Height.keyToObject(obj.height).getFeet(),
    heightInches: Height.keyToObject(obj.height).getInches(),
  },
})

const birthYearIn = (obj) => mapKeys(obj, (value, key) => (key === 'birthdate' ? 'birthYear' : key))
const birthYearOut = (obj) =>
  mapKeys(obj, (value, key) => (key === 'birthYear' ? 'birthdate' : key))
const numbersToStrings = (obj) => mapValues(obj, (value) => (isNumber(value) ? `${value}` : value))
const stringsToNullAndNumbers = (obj) =>
  mapValues(obj, (value) => {
    if (!value) return null // captures null, undefined, false, and empty string
    if (!isNaN(+value)) return +value // if the value is a number, return as a number
    return value
  })
const nullToUndefined = (obj) => mapValues(obj, (value) => (isNull(value) ? undefined : value))

const sizesIn = flow([numbersToStrings])

const sizesOut = flow([stringsToNullAndNumbers])

export const getSizes = (userId) =>
  authGet(`${services.SIZE_API_URL}/${userId}/latest`, {
    headers: {'X-API-Key': services.SIZE_API_KEY},
  })
    .then(
      onStatus({
        OK: shimJsonData(sizesIn),
      }),
    )
    .then(logBadResponse([404])) // 404s mean that sizes don't exist for this user--OK

export const setSizes = (userId, sizes) =>
  authPost(`${services.SIZE_API_URL}/${userId}`, sizesOut(sizes), {
    headers: {'X-API-Key': services.SIZE_API_KEY},
  })
    .then(
      onStatus({
        OK: shimJsonData(sizesIn),
      }),
    )
    .then(logBadResponse())

const fitProfileIn = flow([birthYearIn, heightIn, numbersToStrings, nullToUndefined])

const fitProfileOut = flow([stringsToNullAndNumbers, heightOut, birthYearOut])

/*
 * Expects:
 *   {
 *     json: {
 *       data: {
 *         fitProfile: {...}
 *       }
 *     }
 *     status: 200,
 *   }
 */

export const getFitProfile = (userId) =>
  authGet(`${services.FIT_PROFILE_API_URL}/${userId}/latest`, {
    headers: {'X-API-Key': services.SIZE_API_KEY},
  })
    .then(
      onStatus({
        OK: shimJsonData(fitProfileIn),
      }),
    )
    .then(logBadResponse([404])) // 404s mean that a fit profile doesn't exist for this user--OK

/*
 * Expects:
 *   {
 *     json: {
 *       data: {
 *         fitProfile: {...}
 *         szies: {...}
 *       }
 *     }
 *     status: 201,
 *   }
 */

export const setFitProfile = (userId, responses) =>
  authPost(`${services.FIT_PROFILE_API_URL}/${userId}`, fitProfileOut(responses), {
    headers: {'X-API-Key': services.SIZE_API_KEY},
  })
    .then(
      onStatus({
        OK: shimJsonData(flow([shim(fitProfileIn, 'fitProfile'), shim(sizesIn, 'sizes')])),
      }),
    )
    .then(logBadResponse())

// Analytics

export const trackFitFlowCompleted = (uniqueId, flowName) =>
  monolithPost('/ajax/completed-fit-flow', {uniqueId, flow: flowName})
